import React from 'react';
import PropTypes from 'prop-types';
import { Editor as SlateEditor } from 'slate-react';
import { Block, Value } from 'slate';
import InsertImages from 'slate-drop-or-paste-images';
import styled, { css } from 'styled-components';
import { BoldIcon, ColumnsThreeEqualIcon, ItalicIcon, PhotoIcon, VideoIcon, LinkIcon } from 'base/components/Icon';
import VideoPlugin from './plugins/video';
import ColumnsPlugin from './plugins/columns';
import Toolbar from './components/Toolbar';
import ToolbarMarkButton from './components/ToolbarMarkButton';
import ToolbarBlockButton from './components/ToolbarBlockButton';
import { onClickVideoBlock } from './plugins/video/utils';
import theme from '../theme';
import { onClickColumnsBlock } from './plugins/columns/utils';
import ImagePlugin from './plugins/image';
import { onClickImageButton } from './plugins/image/utils';
import LinkPlugin, { onClickLink } from './plugins/link';
import ToolbarButton from './components/ToolbarButton';

const EditorWrapper = styled.div``;

const EditorContent = styled.div`
  padding: 0 45px;
  ${props =>
    props.isReadOnly &&
    css`
      margin-bottom: -32px;
    `};
  @media (max-width: ${theme.breakpoints.desktopLarge}) {
    padding: 0 20px;
  }
`;

const Paragraph = styled.p`
  margin: 0;
  padding: 5px 0;
`;

const emptyValue = Value.fromJSON({
  document: {
    nodes: [
      {
        object: 'block',
        type: 'paragraph',
        nodes: [
          {
            object: 'text',
            text: '',
          },
        ],
      },
    ],
  },
});

class Editor extends React.Component {
  state = {
    value: Value.fromJSON(this.props.initialValue || emptyValue),
  };

  editorRef = React.createRef();

  plugins = [
    VideoPlugin(),
    ColumnsPlugin(),
    ImagePlugin({
      handleUpload: this.props.handleImageUpload,
    }),
    InsertImages({
      extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp'],
      insertImage: (editor, file) => editor.insertImageFile(file),
    }),
    LinkPlugin(),
  ];

  schema = {
    document: {
      last: { type: 'paragraph' },
      normalize: (editor, { code, node }) => {
        // Always insert paragraph in the end
        if (code === 'last_child_type_invalid') {
          const paragraph = Block.create('paragraph');
          editor.insertNodeByKey(node.key, node.nodes.size, paragraph);
        }
      },
    },
  };

  handleChange = ({ value }) => {
    this.props.onChange(value);
    this.setState({ value });
  };

  renderMark = (props, editor, next) => {
    const { children, mark, attributes } = props;

    switch (mark.type) {
      case 'bold':
        return <strong {...attributes}>{children}</strong>;
      case 'italic':
        return <em {...attributes}>{children}</em>;
      default:
        return next();
    }
  };

  renderBlock = (props, editor, next) => {
    const { children, node, attributes } = props;
    switch (node.type) {
      case 'paragraph':
        return <Paragraph {...attributes}>{children}</Paragraph>;
      default:
        return next();
    }
  };

  render() {
    const { isReadOnly } = this.props;
    const { value } = this.state;
    return (
      <EditorWrapper>
        {!isReadOnly && (
          <Toolbar contentEditable={false}>
            <ToolbarMarkButton type="bold" icon={<BoldIcon />} editor={this.editorRef.current} label="Bold" />
            <ToolbarMarkButton type="italic" icon={<ItalicIcon />} editor={this.editorRef.current} label="Italic" />
            <ToolbarButton
              onClick={event => onClickLink(event, this.editorRef.current)}
              type="italic"
              icon={<LinkIcon />}
              editor={this.editorRef.current}
              label="Link"
            />
            <ToolbarBlockButton
              type="video"
              icon={<VideoIcon />}
              editor={this.editorRef.current}
              onClick={onClickVideoBlock}
              label="Youtube video"
            />
            <ToolbarBlockButton
              type="columns"
              icon={<PhotoIcon />}
              editor={this.editorRef.current}
              onClick={onClickImageButton}
              label="Image"
            />
            <ToolbarBlockButton
              type="columns"
              icon={<ColumnsThreeEqualIcon />}
              editor={this.editorRef.current}
              onClick={onClickColumnsBlock}
              label="Columns"
            />
          </Toolbar>
        )}
        <EditorContent isReadOnly={isReadOnly}>
          <SlateEditor
            ref={this.editorRef}
            value={value}
            onChange={this.handleChange}
            renderMark={this.renderMark}
            renderBlock={this.renderBlock}
            plugins={this.plugins}
            schema={this.schema}
            readOnly={isReadOnly}
            autoFocus
          />
        </EditorContent>
      </EditorWrapper>
    );
  }
}
Editor.propTypes = {
  isReadOnly: PropTypes.bool,
  onChange: PropTypes.func,
  initialValue: PropTypes.objectOf(PropTypes.any),
  handleImageUpload: PropTypes.func,
};
Editor.defaultProps = {
  isReadOnly: false,
  onChange: () => {},
  initialValue: {},
  handleImageUpload: () => {},
};

export default Editor;
